/**
 * ============================================================
 * DIBAI 18K — Hero Slider Premium
 * Asset: hero-dibai.js
 * Versión: 1.0.0
 *
 * Funcionalidades:
 * - Autoplay con pausa en hover / out-of-view
 * - Transiciones: fade, crossfade, zoom-fade
 * - Animación por items con stagger delay
 * - Swipe táctil + Mouse drag
 * - Navegación por teclado
 * - Dots + Flechas
 * - Loop infinito
 * - Intersection Observer para visibilidad
 * - Shopify Theme Editor events
 * ============================================================
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────
  // CLASE PRINCIPAL
  // ─────────────────────────────────────────
  class HeroDibai {
    /**
     * @param {HTMLElement} el
     */
    constructor(el) {
      this.el          = el;
      this.sectionId   = el.dataset.sectionId;
      this.slides      = Array.from(el.querySelectorAll('.hero-dibai__slide'));
      this.dots        = Array.from(el.querySelectorAll('[data-hero-dot]'));
      this.prevBtn     = el.querySelector('[data-hero-prev]');
      this.nextBtn     = el.querySelector('[data-hero-next]');
      this.track       = el.querySelector('.hero-dibai__track');

      this.count       = this.slides.length;
      this.current     = 0;
      this.isAnimating = false;
      this.isVisible   = true;

      // Settings desde data-attributes
      this.opt = {
        autoplay:       el.dataset.autoplay === 'true',
        speed:          parseInt(el.dataset.autoplaySpeed, 10) || 5000,
        loop:           el.dataset.loop === 'true',
        pauseOnHover:   el.dataset.pauseOnHover === 'true',
        pauseOutOfView: el.dataset.pauseOutOfView === 'true',
        keyboardNav:    el.dataset.keyboardNav === 'true',
        transition:     el.dataset.transition || 'fade',
      };

      this._timer      = null;
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._mouseStartX = 0;
      this._isDragging  = false;

      if (this.count < 2) {
        // Single slide: solo animar el contenido del primer slide
        this._animateContent(this.slides[0]);
        return;
      }

      this._init();
    }

    // ─────────────────────────────────────────
    // INICIALIZACIÓN
    // ─────────────────────────────────────────
    _init() {
      // Animación inicial del slide 0
      this._animateContent(this.slides[0]);

      // Controles
      this.prevBtn?.addEventListener('click', () => this.prev());
      this.nextBtn?.addEventListener('click', () => this.next());

      // Dots
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.heroDot, 10);
          this.goTo(idx);
        // Reproducir video del slide inicial
        setTimeout(() => {
          const activeSlide = this.slides[this.current];
          const vid = activeSlide && activeSlide.querySelector('.hero-dibai__bg-video');
          if (vid) vid.play().catch(() => {});
        }, 100);
        });
      });

      // Autoplay
      if (this.opt.autoplay) this._startAutoplay();

      // Pause on hover
      if (this.opt.pauseOnHover) {
        this.el.addEventListener('mouseenter', () => this._stopAutoplay());
        this.el.addEventListener('mouseleave', () => {
          if (this.opt.autoplay && this.isVisible) this._startAutoplay();
        });
      }

      // Visibility (Intersection Observer)
      if (this.opt.pauseOutOfView && 'IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isVisible = entry.isIntersecting;
              if (this.isVisible && this.opt.autoplay) {
                this._startAutoplay();
              } else {
                this._stopAutoplay();
              }
            });
          },
          { threshold: 0.3 }
        );
        this._observer.observe(this.el);
      }

      // Touch / swipe
      this.el.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
      this.el.addEventListener('touchend',   this._onTouchEnd.bind(this),   { passive: true });

      // Mouse drag
      this.el.addEventListener('mousedown',  this._onMouseDown.bind(this));
      this.el.addEventListener('mouseup',    this._onMouseUp.bind(this));
      this.el.addEventListener('mouseleave', this._onMouseLeave.bind(this));

      // Keyboard navigation
      if (this.opt.keyboardNav) {
        this.el.setAttribute('tabindex', '0');
        this.el.addEventListener('keydown', this._onKeydown.bind(this));
      }

      // Apply per-device visibility for arrows/dots (CSS vars → pointer-events fix)
      this._applyDeviceVisibility();
      window.addEventListener('resize', this._applyDeviceVisibility.bind(this));
    }

    /**
     * Lee las CSS vars de visibilidad por dispositivo y aplica
     * pointer-events: none cuando el elemento debe estar oculto.
     * Esto resuelve la limitación de que pointer-events no acepta CSS vars.
     */
    _applyDeviceVisibility() {
      const style = getComputedStyle(this.el);
      const w = window.innerWidth;

      // Leer CSS vars según breakpoint actual
      // Valores: '1' = mostrar, '0' = ocultar
      let arrowVar, dotVar;
      if (w < 750) {
        arrowVar = style.getPropertyValue('--hd-arrows-mobile').trim();
        dotVar   = style.getPropertyValue('--hd-dots-mobile').trim();
      } else if (w < 990) {
        arrowVar = style.getPropertyValue('--hd-arrows-tablet').trim();
        dotVar   = style.getPropertyValue('--hd-dots-tablet').trim();
      } else {
        arrowVar = style.getPropertyValue('--hd-arrows-desktop').trim();
        dotVar   = style.getPropertyValue('--hd-dots-desktop').trim();
      }

      const showArrows = arrowVar.trim() !== '0';
      const showDots   = dotVar.trim()   !== '0';

      // Arrows — display:none es más limpio que opacity
      this.el.querySelectorAll('.hero-dibai__arrow').forEach((btn) => {
        btn.style.display   = showArrows ? '' : 'none';
        btn.setAttribute('aria-hidden', String(!showArrows));
        btn.setAttribute('tabindex', showArrows ? '0' : '-1');
      });

      // Dots
      const dotsEl = this.el.querySelector('.hero-dibai__dots');
      if (dotsEl) {
        dotsEl.style.display = showDots ? '' : 'none';
        dotsEl.setAttribute('aria-hidden', String(!showDots));
      }
    }

    // ─────────────────────────────────────────
    // NAVEGACIÓN
    // ─────────────────────────────────────────
    goTo(index) {
      if (this.isAnimating || index === this.current) return;

      const total = this.count;
      let next = index;

      if (!this.opt.loop) {
        next = Math.max(0, Math.min(total - 1, index));
        if (next === this.current) return;
      } else {
        // Loop: wrap around
        next = ((index % total) + total) % total;
      }

      this._transition(this.current, next);
    }

    next() {
      this.goTo(this.current + 1);
    }

    prev() {
      this.goTo(this.current - 1);
    }

    // ─────────────────────────────────────────
    // TRANSICIÓN
    // ─────────────────────────────────────────
    _transition(fromIdx, toIdx) {
      if (this.isAnimating) return;

      this.isAnimating = true;

      const fromSlide = this.slides[fromIdx];
      const toSlide   = this.slides[toIdx];

      // Remover is-active del actual
      fromSlide.classList.remove('is-active');
      fromSlide.setAttribute('aria-hidden', 'true');
      fromSlide.setAttribute('aria-current', 'false');

      // Reset animaciones del slide saliente
      this._resetContentAnim(fromSlide);

      // Activar el nuevo slide
      toSlide.classList.add('is-active');
      toSlide.setAttribute('aria-hidden', 'false');
      toSlide.setAttribute('aria-current', 'true');

      this.current = toIdx;

      // Pausar video del slide saliente
      const fromVideo = fromSlide.querySelector('.hero-dibai__bg-video');
      if (fromVideo) { fromVideo.pause(); }

      // Reproducir video del slide entrante (necesita estar visible primero)
      const toVideo = toSlide.querySelector('.hero-dibai__bg-video');
      if (toVideo) {
        toVideo.currentTime = 0;
        setTimeout(function () {
          toVideo.play().catch(function () {});
        }, 50);
      }

      // Actualizar dots
      this._updateDots(toIdx);

      // Animar contenido del slide entrante
      // Esperar al próximo frame para que el fade se inicie primero
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._animateContent(toSlide);
        });
      });

      // Duración de la transición (CSS la maneja, solo gestionamos el flag)
      const duration = parseInt(
        getComputedStyle(this.el).getPropertyValue('--hd-transition') || '700',
        10
      );

      setTimeout(() => {
        this.isAnimating = false;
      }, duration + 50);
    }

    // ─────────────────────────────────────────
    // ANIMACIÓN DE CONTENIDO
    // ─────────────────────────────────────────
    _animateContent(slide) {
      if (!slide) return;

      const items = Array.from(slide.querySelectorAll('[data-anim-item]'));
      const anim  = slide.dataset.contentAnim || 'slide-up';
      const delay = parseInt(slide.dataset.contentDelay, 10) || 80;

      if (anim === 'none') {
        items.forEach((item) => {
          item.classList.add('is-animated');
        });
        return;
      }

      // Stagger: cada item con delay incremental
      items.forEach((item, i) => {
        // Asegurar que empieza en estado invisible
        item.classList.remove('is-animated');

        setTimeout(() => {
          item.classList.add('is-animated');
        }, i * delay);
      });
    }

    _resetContentAnim(slide) {
      if (!slide) return;
      slide.querySelectorAll('[data-anim-item]').forEach((item) => {
        item.classList.remove('is-animated');
      });
    }

    // ─────────────────────────────────────────
    // DOTS
    // ─────────────────────────────────────────
    _updateDots(activeIdx) {
      this.dots.forEach((dot, i) => {
        const isActive = i === activeIdx;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    // ─────────────────────────────────────────
    // AUTOPLAY
    // ─────────────────────────────────────────
    _startAutoplay() {
      this._stopAutoplay();
      this._timer = setInterval(() => {
        this.next();
      }, this.opt.speed);
    }

    _stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    // ─────────────────────────────────────────
    // TOUCH / SWIPE
    // ─────────────────────────────────────────
    _onTouchStart(e) {
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
    }

    _onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      const dy = e.changedTouches[0].clientY - this._touchStartY;

      // Solo procesar swipe horizontal (ignorar scroll vertical)
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;

      if (dx < 0) {
        this.next();
      } else {
        this.prev();
      }

      // Reiniciar autoplay tras interacción táctil
      if (this.opt.autoplay && this.isVisible) {
        this._stopAutoplay();
        this._startAutoplay();
      }
    }

    // ─────────────────────────────────────────
    // MOUSE DRAG
    // ─────────────────────────────────────────
    _onMouseDown(e) {
      this._mouseStartX = e.clientX;
      this._isDragging  = true;
      this.el.style.cursor = 'grabbing';
    }

    _onMouseUp(e) {
      if (!this._isDragging) return;
      this._isDragging = false;
      this.el.style.cursor = '';

      const dx = e.clientX - this._mouseStartX;
      if (Math.abs(dx) < 40) return;

      if (dx < 0) {
        this.next();
      } else {
        this.prev();
      }

      if (this.opt.autoplay && this.isVisible) {
        this._stopAutoplay();
        this._startAutoplay();
      }
    }

    _onMouseLeave() {
      if (this._isDragging) {
        this._isDragging = false;
        this.el.style.cursor = '';
      }
    }

    // ─────────────────────────────────────────
    // TECLADO
    // ─────────────────────────────────────────
    _onKeydown(e) {
      if (e.key === 'ArrowLeft')  { this.prev(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { this.next(); e.preventDefault(); }
    }

    // ─────────────────────────────────────────
    // DESTROY
    // ─────────────────────────────────────────
    destroy() {
      this._stopAutoplay();
      this._observer?.disconnect();
    }
  }

  // ─────────────────────────────────────────
  // INICIALIZACIÓN
  // ─────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('.hero-dibai').forEach((el) => {
      if (!el._heroDibai) {
        el._heroDibai = new HeroDibai(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ─────────────────────────────────────────
  // SHOPIFY THEME EDITOR
  // ─────────────────────────────────────────
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.hero-dibai');
    if (el) {
      el._heroDibai?.destroy();
      el._heroDibai = new HeroDibai(el);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('.hero-dibai');
    if (el?._heroDibai) {
      el._heroDibai.destroy();
      el._heroDibai = null;
    }
  });

  // Re-init al cambiar un bloque en el editor
  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest('.hero-dibai');
    if (!section?._heroDibai) return;

    const slide = e.target.closest('.hero-dibai__slide');
    if (slide) {
      const idx = parseInt(slide.dataset.index, 10);
      section._heroDibai.goTo(idx);
    }
  });

})();

/* ─────────────────────────────────────────
   VIDEO DE FONDO — Inicio inmediato agresivo
   ───────────────────────────────────────── */
(function () {
  'use strict';

  function setupVideo(video) {
    if (video.dataset.bgVideoInit) return;
    video.dataset.bgVideoInit = '1';

    // Garantizar atributos necesarios para autoplay
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    function forcePlay() {
      if (!video.paused) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Si falla (política del navegador), reintentar en 500ms
          setTimeout(forcePlay, 500);
        });
      }
    }

    // Intentar reproducir en cada evento posible
    ['loadedmetadata','loadeddata','canplay','canplaythrough'].forEach(function (evt) {
      video.addEventListener(evt, forcePlay, { once: false });
    });

    // Si el video se pausa solo (buffering), reanudar
    video.addEventListener('pause', function () {
      if (!video.ended) {
        setTimeout(forcePlay, 100);
      }
    });

    // Intentar ahora mismo
    forcePlay();

    // Reintentar cada segundo durante los primeros 5s por si el navegador bloqueó
    var attempts = 0;
    var interval = setInterval(function () {
      forcePlay();
      attempts++;
      if (attempts >= 5 || !video.paused) clearInterval(interval);
    }, 1000);
  }

  function initAll() {
    document.querySelectorAll('.hero-dibai__bg-video').forEach(setupVideo);
  }

  // Ejecutar lo antes posible
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // También al cargar la página completa (por si los videos cargan tarde)
  window.addEventListener('load', initAll);

  // Editor Shopify
  document.addEventListener('shopify:section:load', function () {
    setTimeout(initAll, 50);
  });

  // Observar el DOM por si aparecen nuevos videos
  if ('MutationObserver' in window) {
    new MutationObserver(function () { initAll(); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  // Reactivar en interacción del usuario (iOS requiere gesto)
  ['touchstart','click','keydown'].forEach(function (evt) {
    document.addEventListener(evt, function handler() {
      initAll();
      document.removeEventListener(evt, handler);
    }, { once: true, passive: true });
  });
})();
